import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Database, PlayCircle, RotateCcw, Square, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { useFmt } from "@/components/v52/Bits";
import { useT52 } from "@/i18n/v52";

const ACTIVE = ["QUEUED", "BACKUP", "RESTORE_CHECK", "PREFLIGHT", "IMPORTING", "VERIFYING"];
const TONE = { verified: "ok", writing: "unk", written: "unk", uncertain: "fail", blocked: "fail", pending: "unk" };
const STATE_TONE = { DONE: "ok", BLOCKED: "fail", FAILED: "fail", PARTIAL: "fail" };
const short = (s, n = 12) => (s ? `${String(s).slice(0, n)}…` : "—");

export async function downloadRunReport(rid) {
  const r = await api.get(`/import-ops/runs/${rid}/report`);
  const blob = new Blob([JSON.stringify(r.data, null, 1)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `fundio-import-jobbericht-${rid}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function LightCard({ light }) {
  const { t52 } = useT52();
  return (
    <section className={`card-surface rounded-xl f52-ops-light ${light.level}`} data-testid="ops-light" data-level={light.level}>
      <span className="dot" aria-hidden="true" />
      <div>
        <strong data-testid="ops-light-level">{t52(`ops_l_${light.level}`)}</strong>
        <p data-testid="ops-light-code">{t52(`ops_lc_${light.code}`)}</p>
      </div>
    </section>
  );
}

function Tile({ label, value, testid, warn }) {
  return (
    <div className="card-surface rounded-xl" data-testid={testid}>
      <small>{label}</small><strong className={warn ? "f52-imp-warn" : ""}>{value ?? "—"}</strong>
    </div>
  );
}

export function CountTiles({ health, lastOk }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const c = lastOk || {};
  const last = health ? (health.ok ? fmt(health.at) : `${fmt(health.at)} · ${t52("ops_c_failed", { e: health.error })}`) : t52("ops_c_none");
  return (
    <div className="f52-ops-tiles-wrap">
      <div className="f52-ops-tiles">
        <Tile label={t52("ops_c_real_orgs")} value={c.real_orgs} testid="ops-count-real-orgs" />
        <Tile label={t52("ops_c_real_locs")} value={c.real_locations} testid="ops-count-real-locs" />
        <Tile label={t52("ops_c_qa_orgs")} value={c.qa_orgs} testid="ops-count-qa-orgs" />
        <Tile label={t52("ops_c_qa_locs")} value={c.qa_locations} testid="ops-count-qa-locs" />
        <Tile label={t52("ops_c_last_ok")} value={lastOk ? fmt(lastOk.at) : t52("ops_c_none")} testid="ops-last-ok" />
        <Tile label={t52("ops_c_last")} value={last} testid="ops-last-check" warn={health && !health.ok} />
      </div>
      <p className="f52-imp-muted">{t52("ops_c_note")}</p>
    </div>
  );
}

function ProofLine({ kind, p }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const ok = p?.ok === true;
  const dbOnly = ok && kind === "app" && p.complete !== true;
  const artifactOnly = ok && kind === "app" && p.complete === true && p.media_evidence === "artifact";
  const detail = ok ? `${p.ref || ""} · ${fmt(p.created_at)}` : (p?.errors || []).join("; ") || t52("ops_c_none");
  return (
    <li data-testid={`ops-ready-${kind}`}>
      <div>
        <h3>{t52(`ops_r_${kind}`)}</h3><p className={ok ? "" : "f52-imp-warn"}>{detail}</p>
        {dbOnly && <p className="f52-imp-warn" data-testid={`ops-ready-${kind}-media`}>{[t52("ops_r_media_open"), ...(p.media_errors || [])].join(" · ")}</p>}
      </div>
      <span className="f52-chip" data-testid={`ops-ready-${kind}-status`}>{t52(dbOnly ? "ops_r_db_only" : artifactOnly ? "ops_r_media_artifact" : ok ? "ops_r_ok" : "ops_r_missing")}</span>
    </li>
  );
}

export function Readiness({ r }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  if (!r) return null;
  return (
    <div className="f52-ops-tiles-wrap" data-testid="ops-readiness">
      <h3>{t52("ops_r_title")}</h3>
      <ul className="f52-imp-steps">
        <ProofLine kind="control" p={r.control} />
        <ProofLine kind="app" p={r.app} />
      </ul>
      <p className="f52-imp-muted">{r.error ? t52("ops_c_failed", { e: r.error }) : fmt(r.checked_at)}</p>
    </div>
  );
}


function StepRow({ k, s }) {
  const { t52 } = useT52();
  const c = (o) => (o ? Object.entries(o).map(([a, b]) => `${a} ${b}`).join(" · ") : "—");
  return (
    <li data-testid={`ops-step-${k}`}>
      <div>
        <h3>{t52(`imp_step_${k}`)}</h3>
        <p>{t52("ops_dry")}: {c(s.dry_run?.counts)}{s.dry_run?.deviations?.length ? ` · ${s.dry_run.deviations.join("; ")}` : ""}</p>
        <p>{t52("ops_receipt")}: {c(s.receipt?.counts)}</p>
        <p>{t52("ops_verify")}: {c(s.verify?.counts)}{s.verify?.deviations?.length ? ` · ${s.verify.deviations.join("; ")}` : ""}</p>
        {s.last_error && <p className="f52-imp-warn">{s.last_error}</p>}
      </div>
      <div className="acts">
        <span className={`f52-chip ${TONE[s.status] || "unk"}`} data-testid={`ops-step-${k}-status`}>{t52(`ops_ss_${s.status}`)}</span>
        <small>{t52("ops_attempts", { n: s.attempts })}</small>
      </div>
    </li>
  );
}

function RunFacts({ run }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const l = run.lock || {};
  return (
    <div className="f52-imp-body">
      <div className="f52-imp-line">
        <span className={`f52-chip ${STATE_TONE[run.state] || "unk"}`} data-testid="ops-run-state">{t52(`ops_s_${run.state}`)}</span>
        <span className="f52-mono" data-testid="ops-run-id">{run.id}</span>
        {run.step && <span>{t52("ops_run_step")}: {t52(`imp_step_${run.step}`)}</span>}
      </div>
      <div className="f52-ops-bar" role="progressbar" aria-valuemin={0} aria-valuemax={run.progress.total} aria-valuenow={run.progress.verified} data-testid="ops-run-progress">
        <span style={{ width: `${(100 * run.progress.verified) / run.progress.total}%` }} />
      </div>
      <p className="f52-imp-line">{t52("ops_run_progress", { n: run.progress.verified, t: run.progress.total })}</p>
      <p className="f52-imp-line" data-testid="ops-run-approval">{run.approval_valid ? t52("ops_run_appr", { t: fmt(run.approval.expires_at) }) : t52("ops_run_appr_none")}</p>
      <p className="f52-imp-line f52-mono" data-testid="ops-run-lock">{l.holder ? t52("ops_run_lock", { h: l.holder, f: l.fence, t: fmt(l.lease_until) }) : t52("ops_run_lock_free")}</p>
      {run.waiting && <p className="f52-imp-note" data-testid="ops-run-waiting"><Loader2 aria-hidden="true" className="f52-spin" />{t52("ops_run_wait", { w: t52(`ops_w_${run.waiting}`) })}</p>}
      {run.error && (
        <p className="f52-imp-warn" data-testid="ops-run-error"><AlertTriangle aria-hidden="true" />{t52("ops_run_err")} · {run.error.category} · {run.error.code}: {run.error.message}</p>
      )}
      {run.resume_point && <p className="f52-imp-line" data-testid="ops-run-resume-point">{t52("ops_run_resume", { s: `${t52(`ops_s_${run.resume_point.state}`)}${run.resume_point.step ? ` · ${t52(`imp_step_${run.resume_point.step}`)}` : ""}` })}</p>}
      <p className="f52-imp-muted f52-mono">{t52("ops_d_pkg", { p: short(run.package_sha256) })} · {run.target?.control_db} · {run.target?.canonical_host}</p>
    </div>
  );
}

export function RunPanel({ run, busy, onStart, onResume, onStop, onReport }) {
  const { t52 } = useT52();
  const active = run && ACTIVE.includes(run.state);
  const actions = (
    <div className="flex flex-wrap gap-2">
      {!active && <button type="button" className="f52-btn f52-btn-primary" disabled={busy} onClick={onStart} data-testid="ops-start"><PlayCircle aria-hidden="true" />{t52("ops_start")}</button>}
      {run && ["BLOCKED", "PARTIAL"].includes(run.state) && <button type="button" className="f52-btn f52-btn-secondary" disabled={busy} onClick={onResume} data-testid="ops-resume"><RotateCcw aria-hidden="true" />{t52("ops_resume")}</button>}
      {active && <button type="button" className="f52-btn f52-btn-secondary" disabled={busy || run.stop_requested} onClick={onStop} data-testid="ops-stop"><Square aria-hidden="true" />{t52("ops_stop")}</button>}
      {run && <button type="button" className="f52-btn f52-btn-secondary" onClick={onReport} data-testid="ops-report"><Download aria-hidden="true" />{t52("ops_report")}</button>}
    </div>
  );
  return (
    <Panel title={t52("ops_run_t")} actions={actions} testid="ops-run-panel">
      {!run ? <p className="f52-imp-muted" data-testid="ops-run-none">{t52("ops_run_none")}</p> : (
        <>
          <RunFacts run={run} />
          <ul className="f52-imp-steps">{run.order.map((k) => <StepRow key={k} k={k} s={run.steps[k]} />)}</ul>
        </>
      )}
    </Panel>
  );
}

export function OpsAlert() {
  const { t52 } = useT52();
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/import-ops/alert").then((r) => setA(r.data)).catch(() => setA(null)); }, []);
  const show = a && (a.level === "red" || a.code === "run_partial");
  if (!show) return null;
  return (
    <p className={`f52-ops-alert ${a.level}`} role="alert" data-testid="dashboard-ops-alert">
      <Database aria-hidden="true" />{t52(a.level === "red" ? "ops_alert_red" : "ops_alert_orange")} {t52(`ops_lc_${a.code}`)}
      <Link to="/import-betrieb" data-testid="dashboard-ops-alert-link">{t52("ops_alert_open")}</Link>
    </p>
  );
}
